import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react"
import Cropper from "cropperjs"
import "cropperjs/dist/cropper.css"
import { CROP_SHAPE } from "types/general.types"

const DEFAULT_CROPPER_OPTIONS = {
  cropShape: "round",
  viewMode: 1,
  zoomable: true,
  scalable: true,
  guides: false,
  highlight: false,
  dragMode: "move",
  autoCropArea: 0.8
}

interface Options extends Cropper.Options {
  cropShape?: CROP_SHAPE;
}

interface ImageCropperProps {
  src: string;
  options?: Options;
  onSave: (croppedImageUrl: string, blob: Blob) => void;
  onDiscard: () => void;
}

const ImageCropper = forwardRef((props: ImageCropperProps, ref) => {
  const {
    src,
    options,
    onSave,
    onDiscard
  } = props

  const [cropper, setCropper] = useState<Cropper | null>(null)

  const imageRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    instantiateCropper()

    return () => {
      if (cropper) {
        cropper.destroy()
        const cropperContainerEl = document.querySelector(".cropper-container")
        if (cropperContainerEl) {
          cropperContainerEl.remove()
        }
      }
    }
  }, [imageRef, cropper])

  function instantiateCropper() {
    if (!imageRef.current || cropper) return

    const newCropper = new Cropper(imageRef.current, {
      ...DEFAULT_CROPPER_OPTIONS,
      ...options,
      viewMode: options?.cropShape === "round" ? 1 : (options?.viewMode ? options.viewMode : DEFAULT_CROPPER_OPTIONS.viewMode),
      autoCropArea: options?.cropShape === "round" ? 1 : (options?.autoCropArea ? options.autoCropArea : DEFAULT_CROPPER_OPTIONS.autoCropArea),
      ready: onCropperReady
    } as Cropper.Options)

    if (newCropper) {
      setCropper(newCropper)
    }
  }

  function onCropperReady() {
    if (options?.cropShape === "round") {
      document.querySelector(".cropper-crop-box")?.classList.add("circular-crop")
    }
  }

  useImperativeHandle(ref, () => ({
    handleSave: () => {
      if (cropper) {
        const croppedCanvas = cropper.getCroppedCanvas()
        croppedCanvas.toBlob((blob) => {
          if (blob) {
            const croppedImageUrl = URL.createObjectURL(blob)
            onSave(croppedImageUrl, blob)
          }
        }, "image/jpeg")
      }
    },
    handleDiscard: () => {
      onDiscard()
    }
  }))

  return (
    <img
      ref={imageRef}
      src={src}
      alt="To be cropped"
      style={{ height: "100%" }}
    />
  )
})

export default ImageCropper
